import { useEffect, useState, useCallback, useMemo, Fragment } from 'react';
import axios from 'axios';
import { MapContainer, TileLayer, Marker, Popup, Polyline, CircleMarker, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { RefreshCw, MapPin, Users, Radio, Route, EyeOff } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const POLL_MS = 30000; // refresh every 30s

// Coloured circular pin per role, drawn as an SVG DivIcon so no image assets needed.
const buildPin = (role, initial) => {
  const bg = role === 'driver' ? '#0ea5e9' : '#10b981'; // sky / emerald
  return L.divIcon({
    className: 'live-fleet-pin',
    html: `<div style="
      width:32px;height:32px;border-radius:50%;
      background:${bg};color:white;display:flex;
      align-items:center;justify-content:center;
      font-size:12px;font-weight:700;
      border:2px solid white;
      box-shadow:0 2px 6px rgba(0,0,0,.3);
    ">${initial}</div>`,
    iconSize: [32, 32],
    iconAnchor: [16, 16],
  });
};

// Automatically fit bounds to pins on data change.
function FitBounds({ pins, trailIds }) {
  const map = useMap();
  useEffect(() => {
    // Include trail points when their trail is visible so the whole path fits.
    const points = [];
    pins.forEach((p) => {
      points.push([p.lat, p.lng]);
      if (trailIds.has(p.shift_id)) {
        (p.trail || []).forEach((t) => points.push([t.lat, t.lng]));
      }
    });
    if (points.length === 0) return;
    if (points.length === 1) {
      map.setView(points[0], 13);
      return;
    }
    const bounds = L.latLngBounds(points);
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 14 });
  }, [pins, trailIds, map]);
  return null;
}

const timeAgo = (iso) => {
  if (!iso) return '—';
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return `${Math.round(diff)}s ago`;
  if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
  return `${Math.round(diff / 86400)}d ago`;
};

/**
 * LiveFleetMap — admin dashboard tile with a real Leaflet map.
 * Auto-polls /api/driver-shifts/live-map every 30 s.
 */
export default function LiveFleetMap() {
  const token = localStorage.getItem('token') || sessionStorage.getItem('token');
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [pins, setPins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [trailIds, setTrailIds] = useState(new Set()); // shift_ids whose trail is visible

  const fetchPins = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/driver-shifts/live-map`, { headers });
      setPins(r.data?.pins || []);
      setLastRefresh(new Date());
      setError(null);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load fleet map');
    } finally { setLoading(false); }
  }, [headers]);

  useEffect(() => {
    fetchPins();
    const id = setInterval(fetchPins, POLL_MS);
    return () => clearInterval(id);
  }, [fetchPins]);

  // Cape Town default view when no pins
  const defaultCentre = [-33.9249, 18.4241];

  return (
    <Card data-testid="live-fleet-map-card">
      <CardHeader className="pb-3 flex flex-row items-center justify-between">
        <div>
          <CardTitle className="text-base flex items-center gap-2">
            <Radio className="h-4 w-4 text-emerald-600 animate-pulse" />
            Live Fleet Map
            <Badge variant="outline" className="ml-2 bg-emerald-50 text-emerald-800 border-emerald-300" data-testid="live-fleet-count">
              <Users className="h-3 w-3 mr-1" /> {pins.length} on duty
            </Badge>
          </CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            {loading ? 'Loading…' : lastRefresh ? `Updated ${timeAgo(lastRefresh.toISOString())} · auto-refresh 30s` : 'Waiting for data…'}
          </p>
        </div>
        <Button size="sm" variant="ghost" onClick={fetchPins} disabled={loading} data-testid="live-map-refresh">
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </CardHeader>
      <CardContent className="p-0">
        {error ? (
          <div className="p-6 text-center text-sm text-rose-600">{error}</div>
        ) : (
          <div style={{ height: 380, width: '100%' }} className="relative">
            <MapContainer
              center={defaultCentre}
              zoom={11}
              scrollWheelZoom
              style={{ height: '100%', width: '100%' }}
            >
              <TileLayer
                attribution='&copy; OpenStreetMap contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <FitBounds pins={pins} trailIds={trailIds} />
              {/* Route trails — polylines connecting each shift's GPS steps */}
              {pins.filter((p) => trailIds.has(p.shift_id) && (p.trail?.length || 0) >= 2).map((p) => {
                const trailColor = p.role === 'driver' ? '#0ea5e9' : '#10b981';
                return (
                  <Fragment key={`trail-${p.shift_id}`}>
                    <Polyline
                      positions={p.trail.map((t) => [t.lat, t.lng])}
                      pathOptions={{ color: trailColor, weight: 4, opacity: 0.75, dashArray: '6, 8' }}
                    />
                    {p.trail.map((t, i) => (
                      <CircleMarker
                        key={`${p.shift_id}-${i}`}
                        center={[t.lat, t.lng]}
                        radius={5}
                        pathOptions={{ color: 'white', fillColor: trailColor, fillOpacity: 1, weight: 2 }}
                      >
                        <Popup>
                          <div className="text-xs">
                            <div className="font-semibold">{p.name} · {t.step.replace(/_/g, ' ')}</div>
                            <div className="text-slate-500">{t.ts ? new Date(t.ts).toLocaleString('en-ZA') : '—'}</div>
                          </div>
                        </Popup>
                      </CircleMarker>
                    ))}
                  </Fragment>
                );
              })}
              {pins.map((p) => (
                <Marker
                  key={p.shift_id}
                  position={[p.lat, p.lng]}
                  icon={buildPin(p.role, (p.name || '?').charAt(0).toUpperCase())}
                >
                  <Popup>
                    <div className="text-xs space-y-1" style={{ minWidth: 180 }}>
                      <div className="font-bold text-sm">{p.name}</div>
                      <div className="flex items-center gap-1 text-slate-600">
                        <span className={`inline-block w-2 h-2 rounded-full ${p.role === 'driver' ? 'bg-sky-500' : 'bg-emerald-500'}`} />
                        {p.role.replace('_', ' ')}
                      </div>
                      {p.vehicle_registration && (
                        <div><b>Vehicle:</b> {p.vehicle_registration}</div>
                      )}
                      <div><b>Step:</b> {p.current_step?.replace(/_/g, ' ')}</div>
                      <div><b>GPS:</b> {p.lat.toFixed(4)}, {p.lng.toFixed(4)}{p.accuracy_m ? ` (±${Math.round(p.accuracy_m)}m)` : ''}</div>
                      <div className="flex items-center gap-1 text-slate-500">
                        <MapPin className="h-3 w-3" /> Last fix {timeAgo(p.last_gps_ts)}
                      </div>
                      <div className="text-slate-500">Started {timeAgo(p.started_at)}</div>
                      {(p.trail?.length || 0) >= 2 && (
                        <button
                          type="button"
                          onClick={() => setTrailIds((prev) => {
                            const next = new Set(prev);
                            if (next.has(p.shift_id)) next.delete(p.shift_id);
                            else next.add(p.shift_id);
                            return next;
                          })}
                          className={`mt-2 w-full inline-flex items-center justify-center gap-1 px-2 py-1 rounded text-[11px] font-semibold border ${
                            trailIds.has(p.shift_id)
                              ? 'bg-rose-50 border-rose-300 text-rose-700 hover:bg-rose-100'
                              : 'bg-cyan-50 border-cyan-300 text-cyan-700 hover:bg-cyan-100'
                          }`}
                          data-testid={`toggle-trail-${p.shift_id}`}
                        >
                          {trailIds.has(p.shift_id)
                            ? <><EyeOff className="h-3 w-3" /> Hide trail</>
                            : <><Route className="h-3 w-3" /> Show trail ({p.trail.length} pts)</>
                          }
                        </button>
                      )}
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
            {!loading && pins.length === 0 && (
              <div className="absolute inset-0 bg-slate-950/40 flex items-center justify-center text-white text-sm z-[500] pointer-events-none">
                <div className="bg-slate-900/90 rounded px-4 py-2 border border-slate-700 pointer-events-auto">
                  No staff currently on duty. Pins appear here when someone clocks in.
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
